import { useState } from 'react';
import { OnboardingStepHeader } from './OnboardingStepHeader';
import { OnboardingFooter } from './OnboardingFooter';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/lib/auth';
import { useOrganization } from '@/lib/organization-context';
import { successToast, errorToast } from '@/components/ui/sonner';
import { INDUSTRY_OPTIONS as CENTRAL_INDUSTRY_OPTIONS } from '@/lib/projectConstants';

interface FirstMissionProps {
  onComplete: (projectId: string) => void;
  onSkip: () => void;
  onBack?: () => void;
}

const INDUSTRY_OPTIONS = CENTRAL_INDUSTRY_OPTIONS.map(opt => ({
  value: opt.value,
  label: opt.label,
}));

export function FirstMission({ onComplete, onSkip }: Omit<FirstMissionProps, 'onBack'>) {
  const { user } = useAuth();
  const { organization } = useOrganization();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({ client_name: '', client_industry: '' });
  const [errors, setErrors] = useState<{ client_name?: string }>({});

  const isFormValid = formData.client_name.trim().length > 0;

  const validateForm = () => {
    const newErrors: { client_name?: string } = {};
    if (!formData.client_name.trim()) newErrors.client_name = 'Client name is required';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user) return;
    if (!validateForm()) return;
    setLoading(true);
    try {
      const { data: project, error } = await supabase
        .from('projects')
        .insert({
          user_id: user.id,
          owner_id: user.id,
          organization_id: organization?.id,
          name: `${formData.client_name.trim()} Proposal`,
          client_name: formData.client_name.trim(),
          client_industry: formData.client_industry || null,
          status: 'active',
          stage: 'created',
        })
        .select()
        .single();
      if (error) throw error;
      successToast('Proposal created', 'Your first proposal is ready.');
      onComplete(project.id);
    } catch (error) {
      console.error('Error creating project:', error);
      errorToast('Error', 'Failed to create proposal. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleIndustrySelect = (value: string) => {
    setFormData({ ...formData, client_industry: value === formData.client_industry ? '' : value });
  };

  return (
    <>
      <OnboardingStepHeader
        step={5}
        totalSteps={5}
        title="Create your first proposal."
        subtitle="Which client are you bidding for? Sorvaine uses this to match your vault content and generate a tailored proposal outline."
      />

      <form onSubmit={handleSubmit}>
        <div style={{ marginBottom: 20 }}>
          <label style={{ fontSize: 12, fontWeight: 500, color: 'rgba(255,255,255,0.6)', marginBottom: 6, display: 'block' }}>
            Client Name <span style={{ color: '#C2541A' }}>*</span>
          </label>
          <input
            value={formData.client_name}
            onChange={(e) => {
              setFormData({ ...formData, client_name: e.target.value });
              if (errors.client_name) setErrors({});
            }}
            placeholder="e.g. UAE Ministry of Finance"
            style={{
              width: '100%',
              padding: '14px 16px',
              border: `1px solid ${errors.client_name ? '#ef4444' : 'rgba(255,255,255,0.1)'}`,
              borderRadius: 8,
              background: 'rgba(255,255,255,0.04)',
              color: '#fff',
              fontSize: 15,
              fontWeight: 500,
              outline: 'none',
            }}
            onFocus={e => {
              e.currentTarget.style.borderColor = 'rgba(194,84,26,0.5)';
              e.currentTarget.style.background = 'rgba(255,255,255,0.06)';
            }}
            onBlur={e => {
              e.currentTarget.style.borderColor = errors.client_name ? '#ef4444' : 'rgba(255,255,255,0.1)';
              e.currentTarget.style.background = 'rgba(255,255,255,0.04)';
            }}
          />
          {errors.client_name && (
            <p style={{ fontSize: 11, color: '#ef4444', marginTop: 4 }}>{errors.client_name}</p>
          )}
        </div>

        <div>
          <label style={{ fontSize: 12, fontWeight: 500, color: 'rgba(255,255,255,0.6)', marginBottom: 6, display: 'block' }}>
            Client Industry <span style={{ color: 'rgba(255,255,255,0.3)', fontWeight: 400 }}>(optional)</span>
          </label>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
            {INDUSTRY_OPTIONS.map((option) => {
              const isSelected = formData.client_industry === option.value;
              return (
                <button
                  key={option.value}
                  type="button"
                  onClick={() => handleIndustrySelect(option.value)}
                  style={{
                    padding: '5px 12px',
                    borderRadius: 999,
                    fontSize: 12,
                    border: `1px solid ${isSelected ? 'rgba(194,84,26,0.4)' : 'rgba(255,255,255,0.1)'}`,
                    color: isSelected ? '#C2541A' : 'rgba(255,255,255,0.5)',
                    background: isSelected ? 'rgba(194,84,26,0.15)' : 'transparent',
                    cursor: 'pointer',
                    transition: 'all 150ms',
                  }}
                >
                  {option.label}
                </button>
              );
            })}
          </div>
        </div>

        <OnboardingFooter onSkip={onSkip} skipLabel="Go to dashboard" loading={loading} disabled={!isFormValid} submitLabel="Create Proposal" />
      </form>
    </>
  );
}
